import React, { useState, useRef, useEffect } from 'react';
import './ServiceFilter.css';
import { FiFilter } from 'react-icons/fi';

const ServiceFilter = ({
  value = [],
  onChange,
  placeholder = 'Filters',
  style,
  services = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [tempValue, setTempValue] = useState(value || []);
  const [searchQueries, setSearchQueries] = useState({
    serviceSearch: '', // renamed key (was serviceName)
    category: '',
  });
  const selectRef = useRef(null);

  // Generate dynamic filter options
  const getFilterOptions = () => {
    const categories = [
      ...new Set((services || []).filter(s => s?.category).map(s => s.category.toString()))
    ].map(c => ({ value: c, label: c }));

    const serviceNames = [
      ...new Set((services || []).filter(s => s?.serviceName).map(s => s.serviceName.toString()))
    ].map(n => ({ value: n, label: n }));

    return [
      {
        title: 'Gender',
        items: [
          { value: 'Female', label: 'Female' },
          { value: 'Male', label: 'Male' },
          { value: 'Both', label: 'Both' },
        ],
      },
      { title: 'Category', items: categories },
      { title: 'Service Name', items: serviceNames },
    ];
  };

  const filterOptions = getFilterOptions();

  const handleToggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next && filterOptions.length > 0) {
      setActiveSection(prev => prev || filterOptions[0]?.title);
      setTempValue(value || []);
    }
  };

  const handleTempOptionChange = optionValue => {
    const newValues = tempValue.includes(optionValue)
      ? tempValue.filter(val => val !== optionValue)
      : [...tempValue, optionValue];
    setTempValue(newValues);
  };

  const handleClear = () => {
    setTempValue([]);
    setSearchQueries({ serviceSearch: '', category: '' });
    if (typeof onChange === 'function') onChange([]);
  };

  const handleClickOutside = event => {
    if (selectRef.current && !selectRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasActiveFilters = () => (value || []).length > 0 || tempValue.length > 0;

  const getFilterCount = sectionTitle => {
    const section = filterOptions.find(opt => opt.title === sectionTitle);
    if (!section) return 0;
    return (tempValue || []).filter(val =>
      section.items.some(item => item.value === val)
    ).length;
  };

  const getActiveSectionItems = () => {
    if (!activeSection) return [];
    const section = filterOptions.find(opt => opt.title === activeSection);
    if (!section) return [];

    let items = section.items || [];

    if (activeSection === 'Service Name' && searchQueries.serviceSearch) {
      const q = searchQueries.serviceSearch.toLowerCase();
      items = items.filter(item => item.label.toLowerCase().includes(q));
    }

    if (activeSection === 'Category' && searchQueries.category) {
      const q = searchQueries.category.toLowerCase();
      items = items.filter(item => item.label.toLowerCase().includes(q));
    }

    return items;
  };

  const handleSearchChange = (section, query) => {
    if (section === 'Service Name') {
      setSearchQueries(prev => ({ ...prev, serviceSearch: query }));
    } else if (section === 'Category') {
      setSearchQueries(prev => ({ ...prev, category: query }));
    }
  };

  useEffect(() => {
    setTempValue(value || []);
  }, [value]);

  return (
    <div className="servicefilter-container" style={style}>
      <div
        className={`servicefilter-header ${hasActiveFilters() ? 'has-filters' : ''}`}
        onClick={handleToggle}
        role="button"
      >
        <FiFilter className="servicefilter-icon" size={20} />
        <span className="servicefilter-placeholder">
          {placeholder}
          {hasActiveFilters() && (
            <span className="servicefilter-count-badge">
              {(value || []).length || tempValue.length}
            </span>
          )}
        </span>
      </div>

      {isOpen && (
        <div className="servicefilter-overlay">
          <div className="servicefilter-panel" ref={selectRef} style={{ width: 700 }}>
            <div className="servicefilter-sidebar">
              <div className="servicefilter-title">Filters</div>
              {filterOptions.map(section => {
                const count = getFilterCount(section.title);
                return (
                  <div
                    key={section.title}
                    className={`servicefilter-sidebar-item ${activeSection === section.title ? 'active' : ''}`}
                    onClick={() => setActiveSection(section.title)}
                    role="button"
                  >
                    <span>{section.title}</span>
                    {count > 0 && <span className="servicefilter-badge">{count}</span>}
                  </div>
                );
              })}
            </div>

            <div className="servicefilter-main">
              <div className="servicefilter-actions">
                <button
                  type="button"
                  className="servicefilter-clear-btn"
                  onClick={handleClear}
                  disabled={tempValue.length === 0 && (value || []).length === 0}
                >
                  CLEAR ALL
                </button>

                <button
                  type="button"
                  className="servicefilter-apply-btn"
                  onClick={() => {
                    if (typeof onChange === 'function') onChange(tempValue);
                    setIsOpen(false);
                  }}
                >
                  APPLY
                </button>
              </div>

              <div className="servicefilter-scrollable">
                {activeSection && (
                  <>
                    <div className="servicefilter-section">
                      <div className="servicefilter-section-title">{activeSection}</div>

                      {(activeSection === 'Service Name' || activeSection === 'Category') && (
                        <div className="servicefilter-search-container">
                          <input
                            type="text"
                            placeholder={`Search ${activeSection.toLowerCase()}...`}
                            value={
                              activeSection === 'Service Name'
                                ? searchQueries.serviceSearch
                                : searchQueries.category
                            }
                            onChange={e => handleSearchChange(activeSection, e.target.value)}
                            className="servicefilter-search-input"
                          />
                        </div>
                      )}
                    </div>

                    <div className="servicefilter-options">
                      {getActiveSectionItems().length === 0 ? (
                        <div className="servicefilter-no-results">
                          {(activeSection === 'Service Name'
                            ? searchQueries.serviceSearch
                            : searchQueries.category)
                            ? 'No results found'
                            : 'No options available'}
                        </div>
                      ) : (
                        getActiveSectionItems().map(option => {
                          const safeId = `servicefilter-${activeSection}-${option.value}`.replace(/\s+/g, '-');
                          return (
                            <div key={option.value} className="servicefilter-option">
                              <input
                                type="checkbox"
                                id={safeId}
                                checked={(tempValue || []).includes(option.value)}
                                onChange={() => handleTempOptionChange(option.value)}
                              />
                              <label htmlFor={safeId}>{option.label}</label>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceFilter;
